<!-- eslint-disable vue/multi-word-component-names -->
<template>
    <div
        ref="menu"
        class="el-menu"
        :class="menuClass"
        :style="{ height: computedHeight, minHeight: computedHeight }"
    >
        <ul
            ref="menubar"
            class="el-menu__bar"
            role="menubar"
            :style="{ height: computedHeight, minHeight: computedHeight }"
            @scroll="debouncedOnMenubarScroll"
        >
            <slot />
        </ul>

        <div
            v-if="mode === 'horizontal' && isOverflow"
            class="el-menu__controls"
            :class="{ 'is-no-gradient': hideControlGradient }"
            :style="{ height: computedHeight, minHeight: computedHeight }"
        >
            <a-button type="ghost" glyph="i-chevron-left--16" @click="scrollMenu(true)" />
            <div class="el-menu__controls-divider" />
            <a-button type="ghost" glyph="i-chevron-right--16" @click="scrollMenu(false)" />
        </div>
    </div>
</template>

<script setup>
import {
    ref, reactive, computed, watch, watchEffect, nextTick,
    onMounted, onBeforeUnmount, provide,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AButton from '@core/components/button/button.vue';
import { useDebouncedFn } from '@core/composables/useDebouncedFn.ts';
import { useResizeObserver } from '@core/composables/useResizeObserver';
import { useSmoothScroll } from '@core/composables/useSmoothScroll';

const props = defineProps({
    mode: { type: String, default: 'vertical' },
    type: { type: String, default: 'primary' },
    modelValue: { type: String, default: '' },
    defaultActive: { type: String, default: '' },
    defaultOpeneds: { type: Array, default: () => [] },
    collapse: Boolean,
    scrollIntoExpanded: Boolean,
    uniqueOpened: Boolean,
    useRouteMatching: Boolean,
    background: { type: String, default: '' },
    noBorderBottom: Boolean,
    hideAllBorders: Boolean,
    height: { type: String, default: '64px' },
});

const emit = defineEmits(['select', 'open', 'close', 'update:modelValue']);
const route = useRoute();
const router = useRouter();
const menu = ref(null);
const menubar = ref(null);

const activeIndex = ref(props.modelValue || props.defaultActive);
const openedMenus = ref([]);
const items = reactive({});
const submenus = reactive({});

const state = reactive({
    itemsList: [],
    contentWidth: 0,
    isOverflow: false,
    hideControlGradient: false,
    margin: 16,
});

const computedHeight = computed(() => (props.mode === 'horizontal' ? props.height : null));
const isOverflow = computed(() => state.isOverflow);
const hideControlGradient = computed(() => state.hideControlGradient);
const menuClass = computed(() => ({
    [`el-menu--${props.mode}`]: true,
    [`el-menu--${props.type}`]: true,
    [`is-${props.background}`]: Boolean(props.background),
    'el-menu--collapse': props.collapse,
    'el-menu--without-submenus': Object.keys(submenus).length === 0,
    'is-borderBottom': props.noBorderBottom || props.hideAllBorders,
    'is-hide-all-borders': props.hideAllBorders,
}));

const { debouncedFn: debouncedOnMenubarScroll, cancel: cancelMenubarScroll } = useDebouncedFn(() => {
    if (props.mode === 'horizontal') updateControlGradient();
}, 25);

useResizeObserver(menu, () => {
    if (props.mode === 'horizontal') updateControls();
});

const { smoothScrollTo } = useSmoothScroll();

function getActiveIndexFromRoute(currentRoute) {
    const { matched, path } = currentRoute;
    if (items[path]) return path;
    for (let i = matched.length - 1; i >= 0; i--) {
        const matchedPath = matched[i].path;
        const regex = matched[i].regex;
        for (const key in items) {
            if (regex.test(key)) return key;
        }
        if (items[matchedPath]) return matchedPath;
    }
    return null;
}

function updateActiveIndex(currentRoute) {
    if (!props.useRouteMatching) return;
    const newActive = getActiveIndexFromRoute(currentRoute);
    if (newActive && newActive !== activeIndex.value) {
        activeIndex.value = newActive;
        updateOpenedMenu();
        scrollToActiveItem();
    }
}

function openMenu(index, indexPath) {
    if (openedMenus.value.includes(index)) return;
    if (props.uniqueOpened) {
        openedMenus.value = indexPath.length <= 1
            ? []
            : openedMenus.value.filter(i => indexPath.includes(i));
    }
    openedMenus.value.push(index);
}

function closeMenu(index) {
    const i = openedMenus.value.indexOf(index);
    if (i !== -1) openedMenus.value.splice(i, 1);
}

function handleItemClick(item) {
    const { index, indexPath } = item;
    if (indexPath.length === 1) {
        for (const key of Object.keys(submenus)) closeMenu(key);
    }
    if (activeIndex.value !== index) {
        emit('update:modelValue', index);
        emit('select', index, indexPath);
        if (!props.modelValue && item.route) {
            router.push(item.route).catch(() => {});
        }
        activeIndex.value = index;
    }
}

function handleSubmenuClick(submenu) {
    const { index, indexPath, $el, slots } = submenu;
    const isOpened = openedMenus.value.includes(index);
    if (isOpened && props.type !== 'primary') {
        closeMenu(index);
        emit('close', index, indexPath);
    } else {
        openMenu(index, indexPath);
        emit('open', index, indexPath);
        if (props.type === 'primary') {
            const defaultSlot = slots?.default?.() || [];
            const firstItem = defaultSlot.find(v => v?.type?.name === 'ElMenuItem');
            if (firstItem?.props?.index && items[firstItem.props.index]) {
                activeIndex.value = firstItem.props.index;
            }
        }
        if (props.scrollIntoExpanded) nextTick(() => scrollToSubmenu($el));
    }
}

function scrollToSubmenu(el) {
    const wrap = menu.value?.parentElement?.querySelector('.el-scrollbar__wrap');
    if (!wrap || !el) return;
    const top = el.getBoundingClientRect().top - menu.value.getBoundingClientRect().top;
    const bottom = top + el.offsetHeight;
    const viewTop = wrap.scrollTop;
    const viewBottom = viewTop + wrap.clientHeight;
    if (el.offsetHeight > wrap.clientHeight || top < viewTop) wrap.scrollTop = top;
    else if (bottom > viewBottom) wrap.scrollTop = bottom - wrap.clientHeight;
}

function scrollToActiveItem() {
    const item = items[activeIndex.value];
    if (item && props.mode === 'vertical') {
        nextTick(() => scrollToSubmenu(item.$el));
    }
}

function updateControls() {
    const list = Array.from(menubar.value?.children || []);
    state.itemsList = list;
    if (!list.length) return;
    const last = list[list.length - 1];
    state.contentWidth = last.offsetLeft + last.offsetWidth + state.margin;
    state.isOverflow = state.contentWidth + 2 * state.margin > menu.value.offsetWidth;
}

function updateControlGradient() {
    const last = state.itemsList[state.itemsList.length - 1];
    const rightOffset = last.getBoundingClientRect().right - menubar.value.getBoundingClientRect().right;
    state.hideControlGradient = rightOffset < 48;
}

function getScrollItem(dir) {
    const bounds = menubar.value.getBoundingClientRect();
    if (dir === 'right') {
        return state.itemsList.find(item => item.getBoundingClientRect().right > bounds.right);
    }
    if (dir === 'left') {
        for (let i = state.itemsList.length - 2; i >= 0; i--) {
            const left = state.itemsList[i].getBoundingClientRect().left;
            if (left < bounds.left) return state.itemsList[i + 1];
        }
        return state.itemsList[0];
    }
}

function scrollMenu(reverse) {
    const current = menubar.value.scrollLeft;
    const target = reverse ? getScrollItem('left') : getScrollItem('right');
    if (target) {
        const offset = target.getBoundingClientRect().left - menubar.value.getBoundingClientRect().left;
        smoothScrollTo(menubar.value, 'horizontal', current + offset, 300);
    }
}

function updateOpenedMenu() {
    const item = items[activeIndex.value];
    if (!item || props.mode === 'horizontal') return;
    nextTick(() => {
        (item.indexPath || []).forEach(i => {
            if (submenus[i] && !openedMenus.value.includes(i)) {
                openMenu(i, submenus[i].indexPath);
            }
        });
    });
}

onMounted(() => {
    if (props.useRouteMatching) {
        activeIndex.value = getActiveIndexFromRoute(route);
    }
    emit('select', activeIndex.value);
    nextTick(() => {
        updateOpenedMenu();
        scrollToActiveItem();
        if (props.mode === 'horizontal') updateControls();
    });
});

onBeforeUnmount(cancelMenubarScroll);

watch(() => props.modelValue, val => {
    activeIndex.value = val;
    updateOpenedMenu();
}, { immediate: true });

watch(() => route, updateActiveIndex, { immediate: true });

watch(() => Object.keys(items), () => {
    if (activeIndex.value && items[activeIndex.value]) updateOpenedMenu();
});

watchEffect(() => {
    if (props.defaultOpeneds.length && Object.keys(submenus).length >= props.defaultOpeneds.length && openedMenus.value.length === 0) {
        openedMenus.value = [...props.defaultOpeneds];
    }
});

provide('menuContext', {
    type: props.type,
    mode: computed(() => props.mode),
    collapse: computed(() => props.collapse),
    activeIndex,
    openedMenus,
    items,
    submenus,
    addItem: (item) => items[item.index] = item,
    removeItem: (item) => delete items[item.index],
    addSubmenu: (item) => submenus[item.index] = item,
    removeSubmenu: (item) => delete submenus[item.index],
    openMenu,
    closeMenu,
    handleItemClick,
    handleSubmenuClick,
});
</script>

<style lang="postcss">
/* TODO: scoped */
.el-menu {
    &__bar {
        width: 100%;
        position: relative;
        margin: 0;
        padding: 0;
        overflow: hidden;
        white-space: nowrap;
        list-style: none;
        flex-shrink: 1;
        li {
            list-style: none;
        }
        &__icon {
            line-height: 0;
        }
    }
    .el-menu-item,
    .el-submenu__item {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        -webkit-user-select: none;
        user-select: none;
        cursor: pointer;
        outline: 0;
        &.is-active {
            cursor: default;
        }
    }

    &--horizontal {
        display: flex;
        box-shadow: 0 -1px 0 0 var(--av-brand-light) inset;
        .el-menu-item {
            @mixin body;
            border: none;
            background-color: transparent;
            outline: none;
            cursor: pointer;
            border-radius: 4px;
            position: relative;
            padding: 0 8px;
            display: inline-flex;
            transition: transform 0.5s ease;
            overflow: visible;
            align-items: center;
            height: 32px;
            flex: 0 0 auto;
            color: var(--av-fixed-primary);
            font-weight: 600;
            letter-spacing: 0.3px;
            &:hover {
                background-color: var(--av-brand-secondary-hover);
            }
            &.is-focus {
                background-color: var(--av-fixed-focus);
            }
            &.is-active {
                cursor: default;
                background-color: var(--av-brand-secondary-active);
                .el-active-indicator {
                    position: absolute;
                    display: block;
                    bottom: -16px;
                    left: 8px;
                    width: calc(100% - 16px);
                    border-bottom: var(--av-border-md) var(--av-brand-secondary);
                }
            }
            &.is-disabled {
                cursor: default;
                color: var(--av-fixed-lighter);
                pointer-events: none;
            }
            &:not(:disabled).is-active {
                background: transparent;
            }
            &:not(:disabled).is-selected {
                background-color: var(--av-brand-secondary-active);
            }
            &:not(:first-child) {
                margin-inline-start: 16px;
            }
            & > .el-tag {
                border-radius: 8px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: flex-start;
                width: 23px;
                height: 16px;
                .el-tag__text {
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    overflow: unset;
                }
            }
        }
        &.is-borderBottom {
            box-shadow: none;
        }
        &.is-fixed-white {
            background-color: var(--av-inversed-primary);
        }
        &.is-nav-primary {
            background-color: var(--av-nav-primary);
        }
        .el-menu__bar {
            margin: 0 16px;
            height: 64px;
            display: flex;
            align-items: center;
        }
        .el-menu__controls {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-around;
            outline-style: none;
            height: 64px;
            width: 128px;
            :before {
                content: "";
                position: absolute;
                inset-inline-start: -48px;
                pointer-events: none;
                top: 0;
                height: calc(100% - 1px);
                width: 48px;
                background: linear-gradient(
                    to right,
                    transparent,
                    var(--av-inversed-primary) 59%
                );
            }
            &.is-no-gradient:before {
                display: none;
            }
            &-divider {
                width: var(--av-border-width-sm);
                background-color: var(--av-brand-light);
                height: 24px;
            }
        }
    }

    &--vertical {
        max-width: 100%;
        vertical-align: middle;
        .is-multiline {
            white-space: normal;
            line-height: 20px;
        }
        &.is-fixed-white {
            &.el-menu--primary,
            &.el-menu--secondary,
            &.el-menu--tertiary {
                background-color: var(--av-inversed-primary);
            }
        }
        &.is-nav-primary {
            &.el-menu--primary,
            &.el-menu--secondary,
            &.el-menu--tertiary {
                background-color: var(--av-nav-primary);
            }
        }
        &.is-hide-all-borders {
            &.el-menu--primary,
            &.el-menu--secondary,
            &.el-menu--tertiary {
                .el-menu__bar > li {
                    border-top: none;
                    box-shadow: none;
                }
            }
        }

        .el-menu-item,
        .el-submenu__item {
            padding: 8px 16px;
            display: flex;
            align-items: center;
            min-height: 48px;
            &:before {
                content: "";
                display: inline-block;
                height: 32px;
                width: 0;
            }
        }

        .el-menu-item__content,
        .el-submenu__item__content {
            width: 100%;
            overflow: hidden;
        }

        &.el-menu--primary {
            .el-menu-item,
            .el-submenu__item {
                color: var(--av-inversed-primary);
                .el-menu__bar-icon {
                    padding-right: 16px;
                }
                &:not(.is-active).is-focus {
                    background-color: var(--av-inversed-lightest);
                }
                &:not(.is-active):hover {
                    background-color: var(--av-inversed-lightest);
                }
                &:not(.is-active):active {
                    background-color: var(--av-nav-secondary);
                }
            }
            .is-root {
                .el-menu-item,
                .el-submenu__item {
                    font-weight: 600;
                    letter-spacing: 0.3px;
                    min-height: 64px;
                    padding-top: 16px;
                    padding-bottom: 16px;
                }
            }
            .el-menu-item.is-active {
                background-color: var(--av-nav-secondary);
                color: var(--av-inversed-primary);
            }
            .el-submenu__arrow {
                display: none;
            }

            .el-menu__bar--level-1 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 16px;
                }
            }
            .el-menu__bar--level-2 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 64px;
                }
            }
            .el-menu__bar--level-3 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 112px;
                }
            }
            .el-menu__bar--level-4 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 160px;
                }
            }
            .el-menu__bar--level-5 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 208px;
                }
            }
        }
        &.el-menu--secondary,
        &.el-menu--tertiary {
            .el-menu-item,
            .el-submenu__item {
                font-weight: 600;
                vertical-align: baseline;
                color: var(--av-brand-primary);
                padding-left: 48px;
                background-clip: padding-box;
                .el-text {
                    color: var(--av-brand-primary);
                }
                .el-menu__bar-icon {
                    display: block;
                    color: var(--av-brand-primary);
                    padding-right: 16px;
                    i {
                        display: block;
                    }
                }
                &__right-icon {
                    color: var(--av-brand-primary);
                }
                &.is-focus,
                &:focus {
                    background-color: var(--av-fixed-focus);
                }
                &.is-disabled {
                    pointer-events: none;
                    color: var(--av-fixed-light);
                    .el-menu__bar-icon {
                        color: var(--av-fixed-lighter);
                    }
                    .el-menu-item__right-icon {
                        color: var(--av-brand-light);
                    }
                }
                &:hover {
                    background-color: var(--av-brand-accent);
                }
                &:active {
                    background-color: var(--av-brand-lightest);
                }
                &.is-root {
                    min-height: 64px;
                    padding-top: 16px;
                    padding-bottom: 16px;
                }
                &.is-active {
                    color: var(--av-fixed-primary);
                }
            }
            .el-submenu__item {
                .is-root {
                    min-height: 63px;
                    padding-top: 16px;
                    padding-bottom: 16px;
                    &:hover {
                        background-color: var(--av-inversed-primary);
                    }
                    &:focus {
                        background-color: var(--av-inversed-primary);
                    }
                }
            }
            &.el-menu--without-submenus {
                .el-menu-item {
                    padding-inline-start: 24px;
                }
            }
            .el-menu__bar {
                padding-bottom: 1px;
                & > li {
                    border-top: 1px var(--av-brand-accent);
                    &:first-child {
                        min-height: 63px;
                        border-top: none;
                    }
                    &:last-child {
                        box-shadow: 0 1px 0 0 var(--av-brand-accent);
                    }
                }
            }
            .el-submenu__arrow {
                color: var(--av-brand-primary);
                margin-right: 16px;
                width: 16px;
                margin-left: -32px;
                right: auto;
            }
            .is-opened > .el-submenu__item .el-submenu__arrow:before {
                transform: rotate(90deg);
            }
            .el-menu__bar--level-1 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 56px;
                }
            }
            .el-menu__bar--level-2 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 88px;
                }
            }
            .el-menu__bar--level-3 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 120px;
                }
            }
            .el-menu__bar--level-4 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 152px;
                }
            }
            .el-menu__bar--level-5 {
                .el-menu-item,
                .el-submenu__item,
                .el-menu-group__title {
                    padding-left: 184px;
                }
            }
        }

        &.el-menu--tertiary {
            .el-menu-item {
                padding-top: 12px !important;
                padding-bottom: 12px !important;
                padding-right: 24px;
                &__title {
                    font-weight: 400 !important;
                    line-height: 24px;
                    color: var(--av-fixed-primary);
                }
                &__content {
                    line-height: 0 !important;
                }
            }
            .el-menu__bar-icon {
                padding-right: 16px !important;
            }
        }

        .multiline-limit--2 {
            .el-menu-item__title {
                overflow: hidden;
                display: -webkit-box;
                line-clamp: 2;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
            }
        }
        .multiline-limit--3 {
            .el-menu-item__title {
                overflow: hidden;
                display: -webkit-box;
                line-clamp: 3;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
            }
        }
        .multiline-limit--4 {
            .el-menu-item__title {
                overflow: hidden;
                display: -webkit-box;
                line-clamp: 4;
                -webkit-line-clamp: 4;
                -webkit-box-orient: vertical;
            }
        }
        .multiline-limit--5 {
            .el-menu-item__title {
                overflow: hidden;
                display: -webkit-box;
                line-clamp: 5;
                -webkit-line-clamp: 5;
                -webkit-box-orient: vertical;
            }
        }
    }
}
</style>
